import { useMemo } from "react";
import { useAtomValue } from "jotai";
import {
  businessFiltersAtom,
  businessMapBoundsAtom,
} from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { api } from "@/lib/api-client";
import type { DetailedListingListItem } from "@/lib/types";

const LIMIT = 6;

type RelaxationTier =
  | "dropFinancials"
  | "dropFinancialsAndDetails"
  | "businessTypeOnly"
  | "locationOnly";

/**
 * Progressively relaxes the user's search filters to find "near-miss"
 * listings when the exact filter combination returns zero results.
 *
 * Tiers (tried in order, stops at first with results):
 *  1. Drop financial range filters — keep business type, status, details, financing booleans
 *  2. Drop financials + details — keep business type, status, financing booleans
 *  3. Business type + location only
 *  4. Location only (no filters)
 */
export function useRelaxedListings(opts: { enabled: boolean }): {
  listings: DetailedListingListItem[];
  tier: RelaxationTier | null;
  isLoading: boolean;
} {
  const filters = useAtomValue(businessFiltersAtom);
  const mapBounds = useAtomValue(businessMapBoundsAtom);

  const bbox = useMemo(() => {
    if (!mapBounds) return undefined;
    return {
      sw: { lat: mapBounds.sw.lat, lng: mapBounds.sw.lng },
      ne: { lat: mapBounds.ne.lat, lng: mapBounds.ne.lng },
    };
  }, [mapBounds]);

  const hasBbox = !!bbox;

  // Tier 1: drop financial ranges, keep everything else
  const tier1Query = useMemo(
    () => ({
      bbox,
      businessTypeIds: filters.businessTypeIds.length > 0 ? filters.businessTypeIds : undefined,
      statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
      listedBy: filters.listedBy.length > 0 ? filters.listedBy : undefined,
      establishmentAge: {
        min: filters.establishmentAgeRange.min ?? undefined,
        max: filters.establishmentAgeRange.max ?? undefined,
      },
      employeeCount: {
        min: filters.employeeCountRange.min ?? undefined,
        max: filters.employeeCountRange.max ?? undefined,
      },
      ownerWorkedHours: {
        min: filters.ownerWorkedHoursRange.min ?? undefined,
        max: filters.ownerWorkedHoursRange.max ?? undefined,
      },
      propertyIncluded: filters.propertyIncluded ?? undefined,
      buyerFinancingAvailable: filters.financingAvailable ?? undefined,
      sbaPrequalified: filters.sbaPrequalified ?? undefined,
      visaQualified: filters.visaQualified ?? undefined,
      limit: LIMIT,
      orderByProperty: "relevance" as const,
      orderByDirection: "desc" as const,
    }),
    [bbox, filters],
  );

  // Tier 2: drop financials + details, keep business type, status, financing booleans
  const tier2Query = useMemo(
    () => ({
      bbox,
      businessTypeIds: filters.businessTypeIds.length > 0 ? filters.businessTypeIds : undefined,
      statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
      buyerFinancingAvailable: filters.financingAvailable ?? undefined,
      sbaPrequalified: filters.sbaPrequalified ?? undefined,
      visaQualified: filters.visaQualified ?? undefined,
      limit: LIMIT,
      orderByProperty: "relevance" as const,
      orderByDirection: "desc" as const,
    }),
    [bbox, filters],
  );

  // Tier 3: business type + location only
  const tier3Query = useMemo(
    () => ({
      bbox,
      businessTypeIds: filters.businessTypeIds.length > 0 ? filters.businessTypeIds : undefined,
      limit: LIMIT,
      orderByProperty: "relevance" as const,
      orderByDirection: "desc" as const,
    }),
    [bbox, filters.businessTypeIds],
  );

  // Tier 4: location only
  const tier4Query = useMemo(
    () => ({
      bbox,
      limit: LIMIT,
      orderByProperty: "relevance" as const,
      orderByDirection: "desc" as const,
    }),
    [bbox],
  );

  const tier1 = api.listings.get.useQuery(tier1Query, {
    enabled: opts.enabled && hasBbox,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const tier1Empty =
    tier1.isSuccess && tier1.data.listings.length === 0;

  const tier2 = api.listings.get.useQuery(tier2Query, {
    enabled: opts.enabled && hasBbox && tier1Empty,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const tier2Empty =
    tier2.isSuccess && tier2.data.listings.length === 0;

  const tier3 = api.listings.get.useQuery(tier3Query, {
    enabled: opts.enabled && hasBbox && tier1Empty && tier2Empty,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const tier3Empty =
    tier3.isSuccess && tier3.data.listings.length === 0;

  const tier4 = api.listings.get.useQuery(tier4Query, {
    enabled: opts.enabled && hasBbox && tier1Empty && tier2Empty && tier3Empty,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const result = useMemo<{
    listings: DetailedListingListItem[];
    tier: RelaxationTier | null;
  }>(() => {
    if (tier1.data && tier1.data.listings.length > 0) {
      return { listings: tier1.data.listings.slice(0, LIMIT), tier: "dropFinancials" };
    }
    if (tier2.data && tier2.data.listings.length > 0) {
      return { listings: tier2.data.listings.slice(0, LIMIT), tier: "dropFinancialsAndDetails" };
    }
    if (tier3.data && tier3.data.listings.length > 0) {
      return { listings: tier3.data.listings.slice(0, LIMIT), tier: "businessTypeOnly" };
    }
    if (tier4.data && tier4.data.listings.length > 0) {
      return { listings: tier4.data.listings.slice(0, LIMIT), tier: "locationOnly" };
    }
    return { listings: [], tier: null };
  }, [tier1.data, tier2.data, tier3.data, tier4.data]);

  const isLoading =
    (opts.enabled && hasBbox && tier1.isLoading) ||
    (tier1Empty && tier2.isLoading) ||
    (tier1Empty && tier2Empty && tier3.isLoading) ||
    (tier1Empty && tier2Empty && tier3Empty && tier4.isLoading);

  return { ...result, isLoading };
}
